import React, { useState } from 'react'
import {
    AuthProvider,
    getMultiFactorResolver,
    MultiFactorError,
    signInWithPopup,
    TotpMultiFactorGenerator,
} from 'firebase/auth'
import { FirebaseError } from 'firebase/app'
import { auth, googleProvider, microsoftProvider } from '../../firebaseConfig'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import MFACodeStep from './MFACodeStep'
import SsoProviderButtons from './SsoProviderButtons'
import { useAppStore } from '../../store/appStore'
import { useSignInAttemptGuard } from '../../hooks/useSignInAttemptGuard'

type GoogleMicrosoftAuthProps = {
    onMfaRequired: () => void
    // Hidden for the same reason as Login.tsx's own free-trial link: nothing to invite
    // someone into that would just 503 on submit while signup is disabled.
    signupEnabled: boolean
}

const GoogleMicrosoftAuth: React.FC<GoogleMicrosoftAuthProps> = ({ onMfaRequired, signupEnabled }) => {
    const { t } = useTranslation()
    const [nextStep, setNextStep] = useState<JSX.Element | null>(null)
    const [error, setError] = useState<string | null>(null)
    const { isPending: isSigningIn, start, cancel } = useSignInAttemptGuard()
    const sessionTag = useAppStore((state) => state.session.tag)

    // Latches on accountNotFound and clears once a new sign-in attempt starts resolving, so
    // the notice survives the sign-out that follows a 404 (see sessionActions.accountNotFound)
    // without getting stuck through a retry. Adjusted during render, not via an effect+setState
    // (this repo's own react-hooks/set-state-in-effect rule flags that as the anti-pattern to
    // avoid — state derived from another value belongs in the render body). Text stays derived
    // fresh from signupEnabled below, so it can't lock in stale wording if that prop resolves
    // later.
    const [showNoAccountNotice, setShowNoAccountNotice] = useState(sessionTag === 'accountNotFound')
    const [prevSessionTag, setPrevSessionTag] = useState(sessionTag)
    if (sessionTag !== prevSessionTag) {
        setPrevSessionTag(sessionTag)
        if (sessionTag === 'accountNotFound') setShowNoAccountNotice(true)
        else if (sessionTag === 'resolving') setShowNoAccountNotice(false)
    }
    const noAccountNotice = showNoAccountNotice
        ? t(signupEnabled ? 'auth.no_account_found' : 'auth.no_account_found_no_signup')
        : null

    const signIn = async (provider: AuthProvider) => {
        // start() itself guards against an overlapping attempt — from this component, the other
        // SSO button, or a password submit elsewhere — since isPending is shared across every
        // interactive sign-in entry point (see useSignInAttemptGuard).
        const attempt = start()
        if (attempt === null) return
        let mfaError: MultiFactorError | null = null
        try {
            console.info('[GoogleAuth] Attempting sign-in...')
            await signInWithPopup(auth, provider)
            console.info('[GoogleAuth] Sign-in successful.')
        } catch (error: unknown) {
            if (error instanceof FirebaseError && error.code === 'auth/multi-factor-auth-required') {
                mfaError = error as MultiFactorError
            } else {
                console.warn('[GoogleAuth] Sign-in failed:', error)
                setError(t('auth.signin_failed'))
                cancel(attempt)
            }
        }

        if (!mfaError) return

        console.info('2FA required')

        try {
            const mfaResolver = getMultiFactorResolver(auth, mfaError)
            const totpFactor = mfaResolver.hints.find((hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID)
            if (totpFactor) {
                console.info('[GoogleAuth] TOTP found, switching to MFA code step')
                onMfaRequired()
                setNextStep(<MFACodeStep mfaResolver={mfaResolver} enrollmentId={totpFactor.uid} />)
            } else {
                throw new Error('Unsupported MFA method detected. Only TOTP is currently supported.')
            }
        } catch (resolverError) {
            console.error('[GoogleAuth] Failed to get MFA resolver:', resolverError)
            cancel(attempt)
        }
    }

    if (nextStep) return nextStep

    // The two never actually overlap (one follows a failed sign-in attempt, the other a
    // successful one), but the notice takes priority if they somehow did.
    const displayedMessage = noAccountNotice ?? error

    return (
        <Stack alignItems="left">
            <SsoProviderButtons
                onSelectGoogle={() => signIn(googleProvider)}
                onSelectMicrosoft={() => signIn(microsoftProvider)}
                disabled={isSigningIn}
                mode="sign_in"
            />
            {displayedMessage && (
                <Typography variant="subtitle2" color="error">
                    {displayedMessage}
                </Typography>
            )}
        </Stack>
    )
}

export default GoogleMicrosoftAuth
